import { Message, ContentBlock, StreamEvent, ContextUsage } from '../types';
import { runTool } from '../tools/office';
import { getBootstrapToken } from './sso';
import { UserProfile } from './userProfile';

// process.env.API_BASE_URL is substituted at build time by webpack's
// DefinePlugin (configured in webpack.config.js). At runtime in the
// browser the literal string is already inlined; the OR fallback only
// applies when the env var was unset at build time (e.g. local dev).
const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:3001';

// Tool-loop circuit breaker. Real customer workflows (building dashboards
// with formatted charts + conditional formatting + freeze panes + tab
// colors + protect cells across 9 facility sheets) can easily exceed 100
// iterations. Bumped to 500 — a defensive ceiling against a truly broken
// model loop (hallucinated circular plan), but not a barrier for
// legitimate work.
//
// Defence in depth above this: ALB 300s timeout per request, conversation
// compaction keeps context bounded, server-side auto-truncation, server
// rejects on total chars > 700K. The frontend circuit breaker is the
// loosest layer — only meant to catch genuinely runaway behaviour.
const MAX_TOOL_LOOP_ITERATIONS = 500;

async function getAuthToken(): Promise<string> {
  return getBootstrapToken();
}

function requestId(): string {
  return 'req-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

export interface TurnHandlers {
  onTextDelta: (text: string) => void;
  onToolUseStart: (name: string) => void;
  onAssistantMessage: (msg: Message) => void;
  onError: (err: string) => void;
  onContextUsage?: (usage: ContextUsage) => void;
  /**
   * 2026-05-28 — fires when the backend can't find one or more attached
   * documentIds in documentStore (typically because the ECS task was
   * replaced or the 4-hour TTL elapsed). The handler should: (a) display
   * a visible warning to the user, (b) prune those documentIds from
   * attachedDocs state so the chip disappears and the user can re-upload.
   */
  onDocumentWarning?: (missing: string[], message: string) => void;
}

export class ChatCancelled extends Error {
  constructor() { super('Chat cancelled by user.'); this.name = 'ChatCancelled'; }
}

/**
 * PR B — thrown when the backend returns 403 with one of the entitlement
 * gate's error codes. Caught at the App.tsx level to render the access wall
 * instead of a generic chat error.
 */
export class AccessNotProvisionedError extends Error {
  reason: 'not_provisioned' | 'suspended';
  contactEmail: string;
  constructor(reason: 'not_provisioned' | 'suspended', message: string, contactEmail: string) {
    super(message);
    this.name = 'AccessNotProvisionedError';
    this.reason = reason;
    this.contactEmail = contactEmail;
  }
}

/**
 * Runs the agent loop: one Bedrock turn → if tool_use, execute tools and call again.
 * Capped at MAX_TOOL_LOOP_ITERATIONS to prevent runaway.
 * Cancellable via the AbortSignal — if signalled, bails immediately and resolves
 * with whatever messages have been produced so far.
 */
export async function runChatTurn(
  initialMessages: Message[],
  handlers: TurnHandlers,
  signal?: AbortSignal,
  options?: { sessionId?: string | null; userProfile?: UserProfile; attachedDocumentIds?: string[]; discussionMode?: boolean }
): Promise<Message[]> {
  const reqId = requestId();
  let messages = initialMessages;

  for (let i = 0; i < MAX_TOOL_LOOP_ITERATIONS; i++) {
    if (signal?.aborted) {
      handlers.onError('Cancelled.');
      return messages;
    }

    // 2026-05-28 — document upload feature. Attach uploaded reference
    // documents on EVERY iteration of the agent loop, not just the first.
    //
    // Earlier version tried to be clever — attach only on i===0 to save
    // tokens, on the theory that Claude would see the doc in iteration 1
    // and respond directly. That broke for any prompt that triggers a
    // tool call first: iteration 1 attaches the doc but Claude responds
    // with a tool_use block (not text). Iteration 2 sends the tool result
    // back — but with NO doc attached. Claude responds in iteration 2
    // saying "I don't see a PDF" because in THAT iteration's message
    // window, it doesn't. The /tax-return-review skill triggered exactly
    // this failure mode (calls get_domain_reference on the first
    // iteration as part of the ATO compliance check).
    //
    // Fix: pass the IDs on every iteration. The backend's bedrock.ts
    // attaches them to the most recent user message each call. Bedrock
    // accepts documents on user messages including tool_result messages.
    // Token cost: one extra ~30K input tokens per tool-call iteration
    // for a 5MB PDF, which prompt caching (already wired with
    // cache_control: ephemeral) amortises to near-zero across a turn.
    const attachedDocumentIds = options?.attachedDocumentIds ?? [];

    const assistant = await streamOneTurn(messages, handlers, reqId, signal, options?.sessionId ?? null, options?.userProfile, attachedDocumentIds, options?.discussionMode ?? false);
    if (!assistant) return messages;

    handlers.onAssistantMessage(assistant);
    messages = [...messages, assistant];

    if (signal?.aborted) {
      handlers.onError('Cancelled.');
      return messages;
    }

    const toolUses = assistant.content.filter(
      (b): b is Extract<ContentBlock, { type: 'tool_use' }> => b.type === 'tool_use'
    );
    if (toolUses.length === 0) return messages;

    const toolResults: ContentBlock[] = [];
    for (const tu of toolUses) {
      if (signal?.aborted) {
        handlers.onError('Cancelled.');
        return messages;
      }
      const res = await runTool(tu.name, tu.input, { toolUseId: tu.id });
      toolResults.push({
        type: 'tool_result',
        tool_use_id: tu.id,
        content: res.content,
        is_error: res.isError,
      });
    }
    messages = [...messages, { role: 'user', content: toolResults }];
  }

  handlers.onError(`Hit tool-loop limit (${MAX_TOOL_LOOP_ITERATIONS} iterations). Conversation may be running away — start a new chat.`);
  return messages;
}

interface PartialBlock {
  type: 'text' | 'tool_use';
  text?: string;
  id?: string;
  name?: string;
  inputBuffer?: string;
}

async function streamOneTurn(
  messages: Message[],
  handlers: TurnHandlers,
  reqId: string,
  signal?: AbortSignal,
  sessionId?: string | null,
  userProfile?: UserProfile,
  attachedDocumentIds: string[] = [],
  discussionMode = false,
): Promise<Message | null> {
  const token = await getAuthToken();

  let resp: Response;
  try {
    // 2026-05-28 — Diagnostic header. Records what the frontend BELIEVED
    // about attachedDocumentIds at send time. Backend logs this next to
    // the [chat-docs] line so future "Claude didn't see the PDF" reports
    // are debuggable from CloudWatch alone without involving the user.
    // Format: "count=N,ids=id1|id2|..."
    const docStateHeader = `count=${attachedDocumentIds.length},ids=${attachedDocumentIds.join('|') || 'none'}`;

    // 2026-05-28 (later) — Build the body object EXPLICITLY rather than via
    // conditional spread. CloudWatch caught a case where the header said
    // count=1 but the body's conditional spread (`...(ids.length > 0 ? ...
    // : {})`) produced no field. Cause was likely a minifier optimisation
    // breaking the spread, or React state batching racing the closure.
    // Either way: build the body with deterministic, non-spread assignment
    // and ALWAYS include attachedDocumentIds (sending an empty array is
    // semantically identical to omitting it — the backend's resolution
    // logic handles both paths).
    const chatBody: Record<string, unknown> = {
      messages,
      sessionId: sessionId ?? null,
      attachedDocumentIds: attachedDocumentIds,  // ALWAYS present — never conditional-spread
    };
    if (userProfile) {
      chatBody.userProfile = userProfile;
    }
    // 2026-06-16 — Discussion / Analysis mode flag. Only sent when on, so a
    // normal build chat's body is byte-identical to before (keeps the
    // backend's behaviour and the prompt cache unchanged for build turns).
    if (discussionMode) {
      chatBody.discussionMode = true;
    }

    console.log('[streamOneTurn] sending chat body — attachedDocumentIds:', attachedDocumentIds, 'header:', docStateHeader);

    resp = await fetch(`${API_BASE_URL}/api/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        Accept: 'text/event-stream',
        'X-Request-ID': reqId,
        'X-PIQ-Doc-State': docStateHeader,
      },
      body: JSON.stringify(chatBody),
      signal,
      credentials: 'include',  // Move 5 follow-up Step 14 — send ALB AWSALB stickiness cookie
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') {
      handlers.onError('Cancelled.');
      return null;
    }
    handlers.onError((e as Error).message || 'Network error');
    return null;
  }

  if (!resp.ok) {
    let msg: string;
    // PR B — entitlement gate. The backend returns 403 with a specific
    // error code when the user isn't provisioned (or the tenant is
    // suspended). Surface that as a typed error so App.tsx renders the
    // access wall instead of a generic chat error.
    if (resp.status === 403) {
      try {
        const j = await resp.clone().json() as { error?: string; message?: string; contactEmail?: string };
        if (j?.error === 'access_not_provisioned' || j?.error === 'tenant_suspended') {
          handlers.onError(j.message ?? 'Access not provisioned.');
          throw new AccessNotProvisionedError(
            j.error === 'tenant_suspended' ? 'suspended' : 'not_provisioned',
            j.message ?? 'Access not provisioned.',
            j.contactEmail ?? 'hello@practiceiq.com.au',
          );
        }
      } catch (e) {
        if (e instanceof AccessNotProvisionedError) throw e;
        /* fall through to generic 403 */
      }
      msg = 'Access denied.';
    }
    else if (resp.status === 401) {
      msg = 'Please sign in again.';
    } else if (resp.status === 429) {
      msg = 'Rate limited — try again in a minute.';
    } else if (resp.status === 413) {
      // Layer-1 alignment: this fires only when post-compaction size
      // legitimately exceeds the hard cap, OR message count exceeded
      // the per-request maximum. Distinguish to give the user actionable
      // guidance instead of the old generic "start a new chat".
      try {
        const j = await resp.clone().json() as { error?: string; messageCount?: number };
        if (j?.messageCount) {
          msg = j.error || `This chat is long — ${j.messageCount} messages. Click "Continue in fresh chat" on this conversation in the side panel to carry your work forward without losing context.`;
        } else {
          msg = j?.error || 'This single turn is too large to fit. Try splitting it into smaller steps, or click "Continue in fresh chat" to carry forward a checkpoint.';
        }
      } catch {
        msg = 'This single turn is too large to fit. Click "Continue in fresh chat" on this conversation to carry forward a checkpoint.';
      }
    } else if (resp.status >= 500) {
      msg = 'Practice IQ service is unavailable.';
    } else {
      msg = `Request failed (${resp.status})`;
    }
    handlers.onError(msg);
    return null;
  }
  if (!resp.body) {
    handlers.onError('No response stream');
    return null;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  const blocks = new Map<number, PartialBlock>();
  let stopReason = 'end_turn';
  let lastTextIndex = -1;

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const payload = line.slice(6).trim();
      if (payload === '[DONE]') continue;

      let ev: StreamEvent;
      try { ev = JSON.parse(payload) as StreamEvent; } catch { continue; }

      if (ev.type === 'error') {
        handlers.onError(ev.message);
        return null;
      } else if (ev.type === 'text') {
        handlers.onTextDelta(ev.text);
        if (lastTextIndex < 0 || !blocks.has(lastTextIndex)) {
          lastTextIndex = blocks.size;
          blocks.set(lastTextIndex, { type: 'text', text: ev.text });
        } else {
          const b = blocks.get(lastTextIndex)!;
          if (b.type === 'text') b.text = (b.text ?? '') + ev.text;
        }
      } else if (ev.type === 'tool_use_start') {
        blocks.set(ev.index, { type: 'tool_use', id: ev.id, name: ev.name, inputBuffer: '' });
        handlers.onToolUseStart(ev.name);
        lastTextIndex = -1;
      } else if (ev.type === 'tool_input_delta') {
        const b = blocks.get(ev.index);
        if (b && b.type === 'tool_use') b.inputBuffer = (b.inputBuffer ?? '') + ev.partial;
      } else if (ev.type === 'message_stop') {
        stopReason = ev.stopReason;
      } else if (ev.type === 'context_usage') {
        handlers.onContextUsage?.({
          inputChars: ev.inputChars,
          rawInputChars: ev.rawInputChars,
          maxInputChars: ev.maxInputChars,
          hardInputCharCap: ev.hardInputCharCap,
          pct: ev.pct,
          rawPct: ev.rawPct,
          messageCount: ev.messageCount,
          maxMessages: ev.maxMessages,
          compactedBlocks: ev.compactedBlocks,
          charsSaved: ev.charsSaved,
        });
      } else if (ev.type === 'document_warning') {
        // 2026-05-28 — attached documentIds the backend couldn't resolve.
        // Surface to the App so it can show a toast + prune the stale chips.
        handlers.onDocumentWarning?.(ev.missing, ev.message);
      }
    }
  }

  const content: ContentBlock[] = [];
  const ordered = [...blocks.entries()].sort((a, b) => a[0] - b[0]);
  for (const [, b] of ordered) {
    if (b.type === 'text' && b.text) {
      content.push({ type: 'text', text: b.text });
    } else if (b.type === 'tool_use') {
      let input: unknown = {};
      const raw = b.inputBuffer ?? '';
      if (raw.trim().length) {
        try { input = JSON.parse(raw); } catch { input = {}; }
      }
      content.push({ type: 'tool_use', id: b.id ?? '', name: b.name ?? '', input });
    }
  }
  if (content.length === 0) content.push({ type: 'text', text: '' });

  return { role: 'assistant', content };
}
